import React from 'react'
import { observer } from 'mobx-react'
import { Alert, Button, LoadingLockupTextBase, SwitchToggle } from '@instacart/tds'
import type { IItemOption, IItemOptionItem } from '~shared/lib/ItemDetail/Types'
import catalogStore from '../../../Stores/CatalogStore'
import GlobalStore from '../../../Stores/GlobalStore'
import { isActionAvailable } from '../../../Utils/kdsActionPolicy'
import { KdsActionId } from '../../../Types/KdsAuthorization'
import { useErrorHandling } from '~shared/hooks/useErrorHandling'
import { FetchStatus } from '~shared/lib/Types/FetchTypes'
import { formatPriceDelta } from '../../../Utils/Formatter'
import styles from '../ManageMenuModal.module.scss'

function isAvailableToday(flag?: boolean): boolean {
  return flag !== false
}

const ItemOptionItemRow: React.FC<{
  siteId: string
  itemId: string
  optionItem: IItemOptionItem
  canEdit: boolean
}> = observer(({ siteId, itemId, optionItem, canEdit }) => {
  const { addError } = useErrorHandling()
  const available = isAvailableToday(optionItem.IsAvailableToday)
  const isToggling = Boolean(catalogStore.togglingItemOptionItemIds.get(optionItem.id))

  const handleToggle = async (): Promise<void> => {
    if (isToggling) return
    try {
      await catalogStore.setItemOptionItemAvailability(siteId, itemId, optionItem.id, !available)
    } catch {
      addError('Failed to update option availability. Please try again.')
    }
  }

  return (
    <div className={styles.optionRow}>
      <div className={styles.optionRowName}>
        {optionItem.Name}
        {/* TODO: Pass currency from KdsConfig when available (defaults to USD) */}
        {formatPriceDelta(optionItem.Price)}
      </div>

      <div className={styles.optionRowRight}>
        {!available && <span className={styles.outOfStockLabel}>Out of stock</span>}
        {canEdit && (
          <SwitchToggle
            isSelected={available}
            onChange={() => void handleToggle()}
            disabled={isToggling}
            label=""
            aria-label={`Toggle ${optionItem.Name} availability`}
          />
        )}
      </div>
    </div>
  )
})

const ItemOptionGroup: React.FC<{
  siteId: string
  itemId: string
  option: IItemOption
  canEdit: boolean
}> = observer(({ siteId, itemId, option, canEdit }) => {
  const { addError } = useErrorHandling()
  const available = isAvailableToday(option.IsAvailableToday)
  const isToggling = Boolean(catalogStore.togglingItemOptionIds.get(option.id))

  const handleToggleAvailability = async (): Promise<void> => {
    if (isToggling) return
    try {
      await catalogStore.setItemOptionAvailability(siteId, itemId, option.id, !available)
    } catch {
      addError('Failed to update option group availability. Please try again.')
    }
  }

  return (
    <div className={styles.optionGroup}>
      <div className={styles.optionGroupHeaderRow}>
        <div className={styles.optionGroupHeader}>{option.Name}</div>

        <div className={styles.optionGroupHeaderRight}>
          {!available && <span className={styles.outOfStockLabel}>Out of stock</span>}
          {canEdit && (
            <SwitchToggle
              isSelected={available}
              onChange={() => void handleToggleAvailability()}
              disabled={isToggling}
              label=""
              aria-label={`Toggle ${option.Name} availability`}
            />
          )}
        </div>
      </div>

      {option.Items?.length ? (
        <div className={styles.optionGroupBody}>
          {option.Items.map(optionItem => (
            <ItemOptionItemRow
              key={optionItem.id}
              siteId={siteId}
              itemId={itemId}
              optionItem={optionItem}
              canEdit={canEdit}
            />
          ))}
        </div>
      ) : (
        <div className={styles.optionGroupEmpty}>No choices.</div>
      )}
    </div>
  )
})

export const ItemOptionsSection: React.FC<{
  siteId: string
  itemId: string
}> = observer(({ siteId, itemId }) => {
  const fetch = catalogStore.itemOptionsFetchByItemId.get(itemId)
  const options = catalogStore.itemOptionsByItemId.get(itemId) ?? []

  const status = fetch?.status ?? FetchStatus.unloaded
  const hasFetched = fetch?.lastFetch != null
  const isFetching = status === FetchStatus.fetching
  const hasError = status === FetchStatus.error
  const hasOptions = options.length > 0
  const canEdit = isActionAvailable(
    GlobalStore.configFetch.data?.actionPolicy,
    KdsActionId.EditItemAvailability
  )

  let content: React.ReactNode = null
  if (hasError) {
    content = (
      <Alert variant="error">
        <span>Unable to load item options.</span>{' '}
        <Button
          variant="secondary"
          onClick={() => catalogStore.fetchItemOptionsForItem(siteId, itemId)}
        >
          Retry
        </Button>
      </Alert>
    )
  } else if (status === FetchStatus.success && !hasOptions) {
    content = <div className={styles.subtleText}>No item options for this item.</div>
  } else if (hasOptions) {
    content = (
      <div className={styles.optionsList}>
        {options.map(option => (
          <ItemOptionGroup
            key={option.id}
            siteId={siteId}
            itemId={itemId}
            option={option}
            canEdit={canEdit}
          />
        ))}
      </div>
    )
  }

  return (
    <div className={styles.itemOptionsSection}>
      {isFetching && !hasFetched && <LoadingLockupTextBase />}

      {content}

      {isFetching && hasFetched && <LoadingLockupTextBase />}
    </div>
  )
})
